import axios from 'axios';
import FileSaver from 'file-saver';
import { wrapAction } from '../../../common/utils/actionLifecycle';
import { getBasicHeaders } from '../../../common/utils/utils';
import { storeAction } from '../../storeActions.constant';
import { storeGetters } from '../../storeGetters.constant';
import { getAxiosCancelTokenSource } from '../../../app.utils';
import { storeMutation } from '../../storeMutations.constant';
import uniqBy from 'lodash/uniqBy';

function recursive(filesAndMediaList, parentId) {
    if (parentId !== null) {
        const obj = filesAndMediaList.find(m => m.id === parentId);
        const childrenItems = filesAndMediaList.filter(m => m.parentId === parentId);
        obj.fileCount = childrenItems.length;
        obj.fileSize = childrenItems.reduce((sum, f) => {
            return sum + f.fileSize;
        }, 0);
        recursive(filesAndMediaList, obj.parentId);
    }
}
export const filesAndMediaState = {
    filesAndMediaList: [],
    canReorder: false,
    canView: false,
    downloadFiles: null,
    downloadFolders: null,
    rootFolderCount: 0,
};
export const filesAndMediaMutations = {
    [storeMutation.setFilesandMediaList]: (state, response) => {
        const { data } = response;
        if (data && data.length !== 0) {
            data.filesAndMediaList.forEach((item) => {
                item.fileSubType = item.fileSubType.toLowerCase();
                item.isEditRow = false;
                item.isNewRow = false;
                item.isDeleteRow = false;
                item.downloadInProgress = false;
                item.lastModifiedDate = new Date(item.lastModifiedDateTime);
            });
        }
        if (
            (state.filesAndMediaList.length > 0 && response.CommunityId !== state.filesAndMediaList[0].communityId) ||
            (data &&
                data.filesAndMediaList &&
                data.filesAndMediaList.length === 0 &&
                !state.filesAndMediaList.length)
        ) {
            state.filesAndMediaList = [];
            state.rootFolderCount = 0;
        }
        if (data && data.filesAndMediaList && data.filesAndMediaList.length > 0) {
            state.filesAndMediaList.push(...data.filesAndMediaList);
            state.filesAndMediaList = uniqBy(state.filesAndMediaList, 'id');
            if (data.filesAndMediaList[0].parentId === null) {
                state.rootFolderCount = data.count;
            }
        }
        state.canReorder = data && data.canReOrder;
        state.canView = data && data.canView;
    },
    [storeMutation.updateFilesAndMediaList]: (state, data) => {
        const list = data.map((item) => {
            item.fileSubType = item.fileSubType.toLowerCase();
            item.lastModifiedDate = new Date(item.lastModifiedDateTime);
            item.isEditRow = false;
            item.downloadInProgress = false;
            return item;
        });

        state.filesAndMediaList
            .filter(o => o.parentId === data[0].parentId)
            .forEach((i) => {
                i.order += data.length;
            });
        state.filesAndMediaList.push(...list);
        recursive(state.filesAndMediaList, data[0].parentId);
    },
    [storeMutation.renameFilesAndMedia]: (state, data) => {
        const obj = state.filesAndMediaList.find(m => m.id === data.id);
        obj.name = data.name;
        obj.lastModifiedDate = new Date(data.lastModifiedDateTime);
        obj.isEditRow = false;
        obj.profileImageUrl = data.profileImageUrl;
        obj.updatedUser = data.updatedUser;
    },

    [storeMutation.deleteFilesAndMedia]: (state, data) => {
        state.filesAndMediaList = state.filesAndMediaList.filter(item => item.id !== data.id);
        if (data.parentId !== null) {
            recursive(state.filesAndMediaList, data.parentId);
        } else {
            state.rootFolderCount -= 1;
        }
    },
    [storeMutation.downloadFiles]: (state, data) => {
        state.featureEvents = data;
    },
};
export const filesAndMediaActions = {
    [storeAction.saveFilesandMediaReorderList]: async ({ state }, list) => {
        return axios({
            url: `${state.baseURL}communities/api/communities/reorder-filesandmedia`,
            headers: getBasicHeaders(),
            data: list,
            method: 'POST',
        });
    },
    [storeAction.getFilesAndMedia]: wrapAction(
        storeAction.getFilesAndMedia,
        async ({ state, commit, rootGetters }, object) => {
            const communityId = await rootGetters[storeGetters.communityIdPromise];

            const requestSource = getAxiosCancelTokenSource();
            const requestObject = {
                actionName: storeAction.getFilesAndMedia,
                requestSource,
            };
            commit(storeMutation.handleCancelableRequest, requestObject, { root: true });

            let url = `${state.baseURL}communities/api/get-all-media-files/${communityId}/${object.pageNumber}/${object.pageSize}`;
            if (object.parentId) {
                url = `${state.baseURL}communities/api/get-media-files/${communityId}/${object.parentId}/${object.pageNumber}/${object.pageSize}`;
            }
            return axios({
                url,
                headers: getBasicHeaders(),
                method: 'GET',
                cancelToken: requestSource.token,
            })
                .then((response) => {
                    commit(storeMutation.removeCancelableRequest, storeAction.getFilesAndMedia, { root: true });
                    commit(storeMutation.setFilesandMediaList, { data: response.data, CommunityId: communityId });
                    return response;
                })
                .catch(() => {
                    commit(storeMutation.setFilesandMediaList, []);
                });
        },
    ),
    [storeAction.createDirectory]: async ({ state, commit, rootGetters }, fileObject) => {
        fileObject.CommunityId = await rootGetters[storeGetters.communityIdPromise];
        return axios({
            url: `${state.baseURL}communities/api/communities/create-directory/${
                rootGetters[storeGetters.communityName]
                }`,
            headers: getBasicHeaders(),
            data: fileObject,
            method: 'POST',
        })
            .then((response) => {
                commit(storeMutation.updateFilesAndMediaList, [response.data]);
                return true;
            })
            .catch((err) => {
                console.log(err);
                if (err.response.data) {
                    throw err.response.data;
                }
            });
    },
    [storeAction.updateDirectory]: async ({ state, commit, rootGetters }, fileObject) => {
        const communityId = await rootGetters[storeGetters.communityIdPromise];
        fileObject.CommunityId = communityId;
        return axios({
            url: `${state.baseURL}communities/api/communities/rename-files`,
            headers: getBasicHeaders(),
            data: fileObject,
            method: 'PUT',
        })
            .then((response) => {
                commit(storeMutation.renameFilesAndMedia, response.data);
                return response;
            })
            .catch((err) => {
                console.log(err);
                throw err;
            });
    },
    [storeAction.deleteFilesAndMedia]: async ({ state, commit, rootGetters }, fileObject) => {
        const communityId = await rootGetters[storeGetters.communityIdPromise];
        fileObject.CommunityId = communityId;
        return axios({
            url: `${state.baseURL}communities/api/communities/delete-storage`,
            headers: getBasicHeaders(),
            data: fileObject,
            method: 'DELETE',
        })
            .then((response) => {
                if (response.data) {
                    commit(storeMutation.deleteFilesAndMedia, fileObject);
                }
                return response;
            })
            .catch((err) => {
                console.log(err);
                throw err;
            });
    },
    [storeAction.getDownloadFolders]: ({ state }, obj) => {
        return axios({
            url: `${state.baseURL}communities/api/communities/Download-filesandmedia`,
            headers: getBasicHeaders(),
            data: obj,
            method: 'POST',
            responseType: 'arraybuffer',
        })
            .then((response) => {
                const blob = new Blob([response.data], { type: 'octet/stream' });
                if (obj.isFolder) {
                    FileSaver.saveAs(blob, `${obj.name}.zip`);
                } else {
                    FileSaver.saveAs(blob, `${obj.name}`);
                }
                return response;
            })
            .catch((err) => {
                console.log(err);
                if (err.response && err.response.status === 404) {
                    const jsonObject = new TextDecoder().decode(err.response.data);
                    const json = JSON.parse(jsonObject);
                    alert(json.message);
                }
                return err;
            });
    },
    [storeAction.getDownloadFile]: ({ state }, obj) => {
        return axios({
            url: `${state.baseURL}communities/api/files-and-media/download-file/${obj.id}`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                if (response.data.fileName) {
                    const binaryString = window.atob(response.data.base64);
                    const bytes = new Uint8Array(binaryString.length);
                    const arrayBuffer = bytes.map((byte, i) => binaryString.charCodeAt(i));
                    const blob = new Blob([arrayBuffer], { type: 'octet/stream' });
                    FileSaver.saveAs(blob, `${response.data.fileName}`);
                }
                return response;
            })
            .catch((err) => {
                console.log(err);
                throw err;
            });
    },
};
export const filesAndMediaGetters = {
    [storeGetters.filesAndMediaList]: state => state.filesAndMediaList,
    [storeGetters.canReorder]: state => state.canReorder,
    [storeGetters.canView]: state => state.canView,
    [storeGetters.downloadFiles]: state => state.downloadFiles,
    [storeGetters.downloadFolders]: state => state.downloadFolders,
    [storeGetters.rootFolderCount]: state => state.rootFolderCount,
};
